import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100/70 cursor-pointer transition-colors"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1.5">
            {article.publishDate} · {article.viewCount.toLocaleString()} 阅读
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 cursor-pointer hover:border-primary-200 transition-all"
      >
        <div className="relative h-52 overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <span className="inline-block px-2 py-0.5 text-xs bg-primary-500 text-background-50 rounded-full mb-2">
              热门推荐
            </span>
            <h3 className="text-lg font-bold text-white line-clamp-2 leading-snug">
              {article.title}
            </h3>
          </div>
        </div>
        <div className="p-4">
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i> {article.commentCount.toLocaleString()}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 cursor-pointer hover:border-primary-200 transition-all"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded-full">
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mt-2">{article.excerpt}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i> {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i> {article.commentCount.toLocaleString()}
          </span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}